
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { CommentSuggestions } from "./comments/CommentSuggestions";

interface CommentFormProps {
  onSubmit: (content: string) => void;
  placeholder?: string;
  buttonText?: string;
  className?: string;
  postId: string;
  postContent?: string;
  postCategory?: string;
}

export const CommentForm = ({ 
  onSubmit, 
  placeholder = "Share some positivity...",
  buttonText = "Send",
  className = "",
  postId,
  postContent = "",
  postCategory
}: CommentFormProps) => {
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to comment",
          variant: "destructive",
        });
        return;
      }

      if (!postId) {
        toast({
          title: "Error",
          description: "Invalid post reference",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('comments')
        .insert([{
          post_id: postId,
          user_id: user.id,
          content: content.trim()
        }]);

      if (error) throw error;

      onSubmit(content);
      setContent("");
      
      toast({
        title: "Comment shared! 💭",
        description: "Your thoughts have been added to the discussion.",
        duration: 5000,
      });

    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: "Error",
        description: "Couldn't submit your comment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col gap-4">
        <Textarea
          placeholder={placeholder}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          className="flex-1 bg-accent resize-none"
          rows={1}
        />
        
        {postContent && (
          <CommentSuggestions
            postContent={postContent}
            postCategory={postCategory}
            onSelectSuggestion={(suggestion) => {
              setContent(suggestion);
              setShowSuggestions(false);
            }}
            isVisible={showSuggestions && !content.trim()}
          />
        )}
        
        <div className="flex items-center justify-end">
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !content.trim()}
            className="px-6"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              buttonText
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
